import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { conformitSyncDefaultValues, type ConformitSyncForm } from "~/utils/conformit-sync-form";
import { restoreWhenLeftEmpty } from "~/utils/form-utils";
import { Card, Collapse, Form, FormItem, NumberField, Select, Switch } from "~/components/ui";

type SchemaOption = { value: string; label: string };

type ConformitSyncConfigFormProps = {
  control: Control<ConformitSyncForm>;
  schemaOptions: SchemaOption[];
  schemasLoading: boolean;
};

export function ConformitSyncConfigForm({ control, schemaOptions, schemasLoading }: ConformitSyncConfigFormProps) {
  const { t } = useTranslation("admin");
  const limitEnabled = useWatch({ control, name: "limitEnabled" });

  const advancedItems = [
    {
      key: "advanced",
      label: t("uploadAdmin.advanced.title"),
      children: (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Controller
              name="skipExisting"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.skipExisting")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.skipExisting")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Controller
              name="limitEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.maxRecords")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.maxRecords")}</span>
            {limitEnabled && (
              <Controller
                name="maxRecords"
                control={control}
                render={({ field }) => (
                  <NumberField
                    value={field.value}
                    // Emptying the field reports null, which the schema rejects even with the limit
                    // off: ignore it while typing and fall back to the default if left empty.
                    onChange={(value) => value !== null && field.onChange(value)}
                    onBlur={restoreWhenLeftEmpty(conformitSyncDefaultValues.maxRecords, field.onChange, field.onBlur)}
                    min={1}
                    max={10000}
                    format={{ maximumFractionDigits: 0 }}
                    size="small"
                    className="w-20"
                  />
                )}
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card title={t("uploadAdmin.sync.conformit.configuration")} size="small">
        <Form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormItem
              label={t("uploadAdmin.form.schema")}
              tooltip={t("uploadAdmin.sync.conformit.schemaHint")}
              className="mb-0"
            >
              <Controller
                name="schemaId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.form.schemaPlaceholder")}
                    allowClear
                    loading={schemasLoading}
                    options={schemaOptions}
                  />
                )}
              />
            </FormItem>
          </div>
        </Form>
      </Card>

      <Collapse items={advancedItems} size="small" />
    </>
  );
}
